<template>
  <section>
    <label class="control-label">Localizar colaboradores</label>
    <input
      type="text"
      class="form-control"
      pattern="[a-zA-Z ]"
      @keydown="keydownAllowOnlyPattern"
      @keyup.down="scrollEmployeesFound('down')"
      @keyup.up="scrollEmployeesFound('up')"
      @keyup.enter="selectEmployeeActive"
      @keyup.esc="reset"
      @blur="resetLazy"
      :placeholder="placeholder"
      :disabled="!employeesLoaded || disabled"
      v-model="term"
    />

    <div ref="autocomplete" v-show="hasEmployeesFound">
      <article
        v-for="(e, index) in employeesFound"
        :key="index"
        @click="select(e)"
        :class="{ selected: e.active }"
      >
        <span v-html="e.termo">{{ e.termo }}</span>
        <span class="pull-right" v-html="e.termoCargo"></span>
        <input type="hidden" :value="e.sshd" />
      </article>
    </div>
  </section>
</template>

<script>
import { capitalize, removeAccents, contains, createRelevanceIndex } from '@/utils/strings'
import { keydownAllowOnlyPattern } from '@/utils/events'

export default {
  props: {
    disabled: {
      type: Boolean,
      default: false
    },
    value: Array
  },
  data() {
    return {
      term: '',
      employeesFound: [],
      employeesSelected: [],
      updatedForModel: false
    }
  },
  watch: {
    term(value) {
      if (value.length === 0) {
        this.reset()
      } else {
        this.searchEmployees(value)
      }
    },
    employeesSelected(value) {
      if (this.updatedForModel) {
        this.updatedForModel = false
      } else {
        this.update()
      }
    },
    value(value) {
      this.employeesSelected = value
      this.updatedForModel = true
    }
  },
  computed: {
    employees () {
      return this.$store.state.escala.funcionarios
    },
    employeesLoaded() {
      return this.employees.length > 0
    },
    placeholder() {
      return this.employeesLoaded
        ? 'Pesquise por nome ou cargo | Pressione enter para adicionar'
        : 'Por favor aguarde carregando lista de funcionários...'
    },
    hasEmployeesFound() {
      return this.employeesFound.length > 0
    },
    employeeIndexActive() {
      return this.employeesFound.map(i => i.active).indexOf(true)
    }
  },
  methods: {
    searchEmployees(term) {
      if (term.length > 3) {
        const { employeesSelected, employees } = this

        // buscando os funcionarios selecionados para excluir da lista de pesquisa
        const selected = employeesSelected.map(i => i.id)

        // criando a lista de pesquisa excluindo os que já foram selecionados
        const searchList = employees.filter(
          ({ id }) => !selected.includes(id)
        )

        const searchValue = term.trim()

        // buscando os funcionarios
        const found = searchList.filter(
          ({ nome, cargo }) =>
            contains(nome, searchValue) || contains(cargo, searchValue)
        )

        // indexando o resultado
        let foundIndexed = found.map(item => {
          const indexNome = createRelevanceIndex(item.nome, searchValue)
          const indexCargo = createRelevanceIndex(item.cargo, searchValue)

          if (indexNome === false) return { ...item, index: indexCargo }
          if (indexCargo === false) return { ...item, index: indexNome }

          return { ...item, index: (indexNome + indexCargo) / 2 }
        })

        // ordenado o resultado
        foundIndexed.sort((item1, item2) => {
          if (item1.index > item2.index) return 1
          if (item1.index < item2.index) return -1

          return 0
        })

        // formatando o resultado
        const result = foundIndexed.map(({ nome, cargo, ...rest }) => {
          let termo = nome
          let termoCargo = cargo

          if (contains(nome, term)) {
            termo = capitalize(this.highlightTerm(nome, searchValue))
          } else {
            const lengthTotal = nome.length + cargo.length
            termoCargo = ''

            if (lengthTotal > 50) {
              const lengthDiff = lengthTotal - 50
              const lengthMax = cargo.length - lengthDiff
              termoCargo =
                capitalize(this.highlightTerm(cargo, searchValue))
                  .substring(0, lengthMax)
                  .trimEnd() + '...'
            } else {
              termoCargo = capitalize(this.highlightTerm(cargo, searchValue))
            }
          }

          return { nome, termo, cargo, termoCargo, active: false, ...rest }
        })

        this.employeesFound = [...result]
        if (this.employeesFound.length > 0) {
          this.employeesFound[0].active = true
        }
      }
    },
    selectEmployeeActive() {
      const {
        hasEmployeesFound,
        employeeIndexActive,
        employeesSelected,
        employeesFound
      } = this

      if (hasEmployeesFound && employeeIndexActive > -1) {
        this.select(employeesFound[employeeIndexActive])
      }
      this.reset()
    },
    select({ id, sshd, nome, cargo }) {
      const { employeesSelected } = this
      if (employeesSelected.map(i => i.nome).includes(nome)) {
        this.$toastr.e('Colaborador já foi selecionado!')
        return
      }

      this.employeesSelected.push({ id, sshd, nome, cargo })
      this.reset()
    },
    scrollEmployeesFound(direction) {
      const { hasEmployeesFound, employeesFound, employeeIndexActive } = this

      if (!hasEmployeesFound) return

      if (employeeIndexActive > -1) {
        // desativar todos os itens da lista
        this.desactiveEmployeesFound()

        const { autocomplete } = this.$refs
        const { scrollTop, scrollHeight } = autocomplete

        if (direction === 'down') {
          if (employeeIndexActive + 1 < employeesFound.length) {
            employeesFound[employeeIndexActive + 1].active = true
            if ((employeeIndexActive + 1) % 9 === 0) {
              if (scrollTop + 369 > scrollHeight) {
                autocomplete.scrollTop = scrollHeight
              } else {
                autocomplete.scrollTop += 369
              }
            }
          } else {
            employeesFound[employeeIndexActive].active = true
          }
        } else {
          if (employeeIndexActive - 1 > -1) {
            autocomplete.scrollTop -= 41
            employeesFound[employeeIndexActive - 1].active = true
          } else {
            employeesFound[employeeIndexActive].active = true
          }
        }
      } else {
        if (direction === 'down') {
          employeesFound[0].active = true
        }
      }
    },
    desactiveEmployeesFound() {
      this.employeesFound.forEach(e => (e.active = false))
    },
    reset() {
      this.term = ''
      this.employeesFound = []
      this.$refs.autocomplete.scrollTop = 0
    },
    resetLazy () {
      setTimeout(() => this.reset(), 200);
    },
    highlightTerm(string, term) {
      const termIndex = removeAccents(string)
        .toLowerCase()
        .indexOf(removeAccents(term).toLowerCase())

      if (termIndex === -1) return string

      return (
        string.substr(0, termIndex) +
        '<b>' +
        string.substr(termIndex, term.length) +
        '</b>' +
        string.substr(termIndex + term.length)
      )
    },
    update() {
      this.$emit('input', [...this.employeesSelected])
    },
    keydownAllowOnlyPattern
  }
}
</script>

<style scoped>
section {
  display: inline-block;
  position: relative;
  width: 100%;
  margin-bottom: 10px;
}

label {
  font-size: 14px;
  font-weight: 600;
  color: #1a202c;
  font-family: BlinkMacSystemFont, -apple-system, Segoe UI, Roboto, Oxygen,
    Ubuntu, Cantarell, Fira Sans, Droid Sans, Helvetica Neue, Helvetica, Arial,
    sans-serif;
  -webkit-font-smoothing: antialiased;
}

div {
  position: absolute;
  border: 1px solid #d4d4d4;
  border-bottom: none;
  border-top: none;
  z-index: 99;
  top: 100%;
  left: 0;
  right: 0;
  overflow: auto;
  height: 379px;
}

div::-webkit-scrollbar {
  width: 5px;
}

div::-webkit-scrollbar-track {
  -webkit-box-shadow: inset 0 0 6px rgba(70, 140, 54, 0.2);
  border-radius: 10px;
}

div::-webkit-scrollbar-thumb {
  border-radius: 10px;
  -webkit-box-shadow: inset 0 0 6px rgba(70, 140, 54, 1);
}

article {
  padding: 10px;
  cursor: pointer;
  background-color: #fff;
  border-bottom: 1px solid #d4d4d4;
}

article:hover {
  background-color: #e9e9e9;
}

article.selected {
  text-decoration: none;
  transition: background-color 0.2s ease-out;
  background-color: rgba(70, 140, 54, 1);
  color: white !important;
}
</style>


// WEBPACK FOOTER //
// src/components/Employee/Search.vue